if (!customElements.get('sticky-atc-bar')) {
  customElements.define(
    'sticky-atc-bar',
    class StickyAtcBar extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.hasVariants = this.dataset.hasVariants === 'true';
        this.button = this.querySelector('[data-sticky-atc-button]');
        this.label = this.querySelector('[data-sticky-atc-label]');
        this.spinner = this.querySelector('.loading__spinner');
        this.variantEl = this.querySelector('[data-sticky-variant]');
        this.priceEl = this.querySelector('[data-sticky-price]');
        this.mediaEl = this.querySelector('.sticky-atc__media');
        this.imageEl = this.querySelector('.sticky-atc__image');
        this.liftedFabs = [];

        this.submitButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        this.productForm = document.getElementById(`product-form-${this.sectionId}`);
        this.variantSelects = document.getElementById(`variant-selects-${this.sectionId}`);

        if (!this.submitButton || !this.productForm || !this.button) return;

        this.onButtonClick = this.onButtonClick.bind(this);
        this.onViewportChange = this.onViewportChange.bind(this);
        this.button.addEventListener('click', this.onButtonClick);
        window.addEventListener('resize', this.onViewportChange);

        this.observeSubmitButton();
        this.subscribeToVariantChanges();
        this.observeSubmitButtonState();
        this.syncButtonState();
      }

      disconnectedCallback() {
        this.button?.removeEventListener('click', this.onButtonClick);
        window.removeEventListener('resize', this.onViewportChange);
        this.intersectionObserver?.disconnect();
        this.mutationObserver?.disconnect();
        this.variantChangeUnsubscriber?.();
        this.clearFloatingActionOffsets();
        document.body.classList.remove('sticky-atc-visible');
      }

      observeSubmitButton() {
        this.intersectionObserver = new IntersectionObserver(
          ([entry]) => {
            // Show only after scrolling past the main ATC (button above viewport).
            // If the button is still below the fold, keep the sticky hidden.
            const scrolledPastButton = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
            this.toggleVisibility(scrolledPastButton);
          },
          {
            root: null,
            threshold: 0,
            rootMargin: '0px',
          }
        );

        this.intersectionObserver.observe(this.submitButton);
      }

      subscribeToVariantChanges() {
        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event?.data?.sectionId !== this.sectionId) return;
          this.updateFromVariantChange(event.data);
        });
      }

      observeSubmitButtonState() {
        this.mutationObserver = new MutationObserver(() => {
          this.syncButtonState();
        });

        this.mutationObserver.observe(this.submitButton, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled', 'class'],
          childList: true,
          subtree: true,
        });
      }

      onViewportChange() {
        if (!this.classList.contains('is-visible')) return;
        this.clearFloatingActionOffsets();
        this.liftFloatingActionButtons();
      }

      toggleVisibility(show) {
        this.classList.toggle('is-visible', show);
        this.toggleAttribute('inert', !show);
        this.setAttribute('aria-hidden', show ? 'false' : 'true');
        document.body.classList.toggle('sticky-atc-visible', show);

        if (show) {
          requestAnimationFrame(() => this.liftFloatingActionButtons());
        } else {
          this.clearFloatingActionOffsets();
        }
      }

      shouldOffsetFloatingActions() {
        return window.matchMedia('(max-width: 989px)').matches;
      }

      /** Keep in sync with sticky-cart-checkout.js */
      static FAB_GAP_PX = -10;

      findBottomRightFloatingActions() {
        if (!this.shouldOffsetFloatingActions()) return [];

        const candidates = Array.from(document.body.querySelectorAll('a, button, div'));

        return candidates.filter((el) => {
          if (el === this || this.contains(el)) return false;
          if (el.closest('sticky-atc-bar, sticky-cart-checkout, cart-drawer, #CartDrawer, .cart-drawer, header, sticky-header')) {
            return false;
          }

          const style = window.getComputedStyle(el);
          if (style.position !== 'fixed') return false;
          if (style.visibility === 'hidden' || style.display === 'none' || style.opacity === '0') return false;

          const rect = el.getBoundingClientRect();
          if (rect.width < 36 || rect.width > 96 || rect.height < 36 || rect.height > 96) return false;

          const fromBottom = window.innerHeight - rect.bottom;
          const fromRight = window.innerWidth - rect.right;

          return fromBottom > -8 && fromBottom < 120 && fromRight > -8 && fromRight < 120;
        });
      }

      liftFloatingActionButtons() {
        if (!this.shouldOffsetFloatingActions()) {
          this.clearFloatingActionOffsets();
          return;
        }

        const measured = this.getBoundingClientRect().height || this.offsetHeight;
        if (!measured) return;

        const clearance = Math.ceil(measured) + this.constructor.FAB_GAP_PX;
        const fabs = this.findBottomRightFloatingActions();

        fabs.forEach((el) => {
          if (el.hasAttribute('data-sticky-cart-lifted')) return;

          if (!el.hasAttribute('data-sticky-atc-bottom')) {
            const currentBottom = Number.parseFloat(window.getComputedStyle(el).bottom);
            const baseBottom = Number.isFinite(currentBottom) ? currentBottom : 16;
            el.setAttribute('data-sticky-atc-bottom', String(baseBottom));
          }

          // Avoid stacking lifts if already raised for this sticky state.
          if (el.hasAttribute('data-sticky-atc-lifted')) {
            const baseBottom = Number.parseFloat(el.getAttribute('data-sticky-atc-bottom'));
            const safeBase = Number.isFinite(baseBottom) ? baseBottom : 16;
            el.style.setProperty('bottom', `${safeBase + clearance}px`, 'important');
            return;
          }

          const baseBottom = Number.parseFloat(el.getAttribute('data-sticky-atc-bottom'));
          const safeBase = Number.isFinite(baseBottom) ? baseBottom : 16;

          el.style.setProperty('bottom', `${safeBase + clearance}px`, 'important');
          el.setAttribute('data-sticky-atc-lifted', 'true');
          this.liftedFabs.push(el);
        });
      }

      clearFloatingActionOffsets() {
        const fabs = this.liftedFabs.length
          ? this.liftedFabs
          : Array.from(document.querySelectorAll('[data-sticky-atc-lifted]'));

        fabs.forEach((el) => {
          const original = el.getAttribute('data-sticky-atc-bottom');
          if (original !== null && original !== '') {
            const parsed = Number.parseFloat(original);
            el.style.bottom = Number.isFinite(parsed) ? `${parsed}px` : original;
            el.removeAttribute('data-sticky-atc-bottom');
          } else {
            el.style.removeProperty('bottom');
          }
          el.removeAttribute('data-sticky-atc-lifted');
        });

        this.liftedFabs = [];
      }

      get variantIdInput() {
        return this.productForm?.querySelector('[name="id"]');
      }

      hasValidVariant() {
        const input = this.variantIdInput;
        if (!input) return false;
        if (input.disabled) return false;
        return Boolean(input.value);
      }

      isMainLoading() {
        return this.submitButton.classList.contains('loading');
      }

      canAddToCart() {
        if (!this.hasValidVariant()) return false;
        if (this.isMainLoading()) return false;
        if (this.submitButton.hasAttribute('disabled')) return false;
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return false;
        return true;
      }

      onButtonClick(event) {
        event.preventDefault();

        if (this.isMainLoading()) return;

        if (this.canAddToCart()) {
          this.submitButton.click();
          return;
        }

        if (!this.hasValidVariant() && this.hasVariants) {
          this.focusVariantPicker();
        }
      }

      focusVariantPicker() {
        this.variantSelects = document.getElementById(`variant-selects-${this.sectionId}`);
        const target = this.variantSelects || this.productForm;
        if (!target) return;

        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        target.scrollIntoView({
          behavior: prefersReducedMotion ? 'auto' : 'smooth',
          block: 'center',
        });

        const focusable = target.querySelector('select, input, button, [tabindex]:not([tabindex="-1"])');
        if (focusable) {
          requestAnimationFrame(() => focusable.focus({ preventScroll: true }));
        }
      }

      updateFromVariantChange({ variant, html }) {
        if (!variant) {
          this.setUnavailableUI();
          this.syncButtonState();
          return;
        }

        this.updateVariantLabel(variant);
        this.updatePrice(html);
        this.updateImage(variant);
        this.syncButtonState();
      }

      updateVariantLabel(variant) {
        if (!this.variantEl) return;

        if (!this.hasVariants) {
          this.variantEl.textContent = '';
          this.variantEl.classList.add('sticky-atc__variant--hidden');
          return;
        }

        const label = variant.public_title || variant.title || (variant.options || []).filter(Boolean).join(' / ');
        if (!label || label === 'Default Title') {
          this.variantEl.textContent = '';
          this.variantEl.classList.add('sticky-atc__variant--hidden');
          return;
        }

        this.variantEl.textContent = label;
        this.variantEl.classList.remove('sticky-atc__variant--hidden');
      }

      updatePrice(html) {
        if (!this.priceEl || !html) return;

        const source = html.getElementById(`price-${this.sectionId}`);
        if (!source) return;

        this.priceEl.innerHTML = source.innerHTML;
      }

      updateImage(variant) {
        if (!this.mediaEl) return;

        const media = variant.featured_media;
        if (!media) return;

        const preview = media.preview_image || media;
        const src = preview?.src;
        if (!src) return;

        const width = 120;
        const sizedSrc = src.includes('?') ? `${src}&width=${width}` : `${src}?width=${width}`;

        if (this.imageEl) {
          this.imageEl.src = sizedSrc;
          this.imageEl.srcset = `${sizedSrc} ${width}w`;
          if (preview.alt) this.imageEl.alt = preview.alt;
        } else {
          const img = document.createElement('img');
          img.className = 'sticky-atc__image';
          img.loading = 'lazy';
          img.width = width;
          img.height = width;
          img.src = sizedSrc;
          img.alt = preview.alt || '';
          this.mediaEl.appendChild(img);
          this.imageEl = img;
        }

        this.mediaEl.classList.remove('sticky-atc__media--empty');
      }

      setUnavailableUI() {
        if (this.variantEl && this.hasVariants) {
          this.variantEl.textContent = '';
          this.variantEl.classList.add('sticky-atc__variant--hidden');
        }

        if (this.priceEl) {
          this.priceEl.innerHTML = '';
        }
      }

      syncButtonState() {
        if (!this.button || !this.label || !this.submitButton) return;

        const hasVariant = this.hasValidVariant();
        const isLoading = this.isMainLoading();
        const mainLabel = this.submitButton.querySelector('span:not(.loading__spinner):not(.sold-out-message)');
        const mainText = mainLabel?.textContent?.trim();

        if (isLoading) {
          this.label.textContent = mainText || this.dataset.addToCartLabel;
          this.button.disabled = true;
          this.button.classList.add('loading');
          this.spinner?.classList.remove('hidden');
          return;
        }

        this.button.classList.remove('loading');
        this.spinner?.classList.add('hidden');

        const canAdd = this.canAddToCart();

        if (!hasVariant && this.hasVariants) {
          this.label.textContent = this.dataset.chooseOptionsLabel || this.dataset.addToCartLabel;
          this.button.disabled = false;
          return;
        }

        if (!canAdd && hasVariant) {
          this.label.textContent =
            mainText || this.dataset.soldOutLabel || this.dataset.unavailableLabel || this.dataset.addToCartLabel;
          this.button.disabled = true;
        } else if (canAdd) {
          this.label.textContent = mainText || this.dataset.addToCartLabel;
          this.button.disabled = false;
        } else {
          this.label.textContent = mainText || this.dataset.unavailableLabel || this.dataset.addToCartLabel;
          this.button.disabled = true;
        }
      }
    }
  );
}
